<template>
  <ValidationObserver @submit="addAssignmentMarks">
    <UiModalContainer
      footer-classes="flex justify-end gap-3"
      :modal-show="modal"
      @handleClick="handleClick"
    >
      <template v-slot:header>
        <span>Add Assignment Marks</span>
      </template>

      <template v-slot>
        <div class="main-content">
          <DetailCard :details="assignmentDetails" />
          <div v-if="isLoading">
            <div class="mt-28">
              <Loader :content="true" />
            </div>
          </div>
          <div v-else-if="!isLoading && isEmpty(currentStudentList)">
            <NoRecordFound class="my-28" />
          </div>
          <TableWrapper v-else id="addAssignmentMarkModal">
            <THead>
              <TRHead>
                <TH v-for="head in tableHeaders" :key="head">{{ head }}</TH>
              </TRHead>
            </THead>
            <TBody>
              <TRBody v-for="(user, index) in currentStudentList" :key="index">
                <TD>
                  <singleUserDisplay
                    :user="user.student"
                    :is-object-concated="false"
                    label="full_name"
                    :image="true"
                  />
                </TD>
                <TD>
                  {{ assignmentDetails.totalMarks }}
                </TD>
                <TD>
                  <UiInput
                    v-model="user['obtained_marks']"
                    :disabled="isDisabled(user.status)"
                    :hide-title="true"
                    type="text"
                    :name="`MARKS ${index}`"
                    title="MARKS"
                    label="MARKS"
                    placeholder="MARKS"
                    :rules="`max_value:${assignmentDetails.totalMarks}|double`"
                    class="bottom"
                    error-custom-classes="absolute -bottom-3.5"
                  />
                </TD>
                <TD>
                  <UiStatus :title="user.status" />
                </TD>
                <TD>
                  <UiInput
                    v-model="user['evaluator_comment']"
                    :hide-title="true"
                    :no-error="true"
                    :disabled="isDisabled(user.status)"
                    type="text"
                    :name="`Comments ${index}`"
                    title="Comment"
                    label="Comment"
                    placeholder="Comment"
                  />
                </TD>
              </TRBody>
            </TBody>
          </TableWrapper>
        </div>
        <Pagination
          v-if="studentCounts > RECORD_LIMIT"
          v-show="!isLoading"
          :record-limit="RECORD_LIMIT"
          class="mb-5 mx-5"
          :max-range="paginationCounts(studentCounts, RECORD_LIMIT)"
          @filterRecord="getStudentList"
        />
      </template>

      <template v-slot:footer>
        <div class="flex gap-3 rtl:gap-x-4">
          <UIButton @click="handleClick('close')">Cancel</UIButton>
          <UIButton
            button="primary"
            :disabled="isDataSubmitting.value || isEmpty(currentStudentList)"
          >
            <span v-if="isDataSubmitting">
              <Loader />
            </span>
            <span v-else>Submit</span>
          </UIButton>
        </div>
      </template>
    </UiModalContainer>
  </ValidationObserver>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import singleUserDisplay from '@src/components/UiElements/SingleUserDisplay.vue'
import Pagination from '@components/BaseComponent/Pagination.vue'
import UiInput from '@components/UiElements/UiInputBox.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import DetailCard from '@src/components/UiElements/DetailCard.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import UiStatus from '@/src/components/UiElements/UiStatus.vue'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import { Form as ValidationObserver } from 'vee-validate'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
import {
  paginationRangeHandler,
  paginationCounts,
  getPageNumberWithRange,
} from '@src/components/BaseComponent/pagination.util.js'
import { fullName } from '@/src/utils/settings/tenant-user.util'

// Props
const props = defineProps({
  modal: { type: Boolean, default: false },
  id: { type: String, default: '' },
  assignmentDetails: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['toggle'])

const store = useStore()

const RECORD_LIMIT = GENERAL_CONSTANTS.MODAL_RECORD_LIMIT

const tableHeaders = ['Student Name', 'Total Marks ', 'Marks', 'Status', 'Comment']
const isLoading = ref(false)
const isDataSubmitting = ref(false)
const studentCounts = ref(0)
const studentsList = ref({})
const currentStudentList = ref([])

const handleClick = (eventName, payload) => {
  if (eventName === 'close') emit('toggle', payload)
}

const getStudentList = async (range) => {
  isLoading.value = true

  let paginationPayload = paginationRangeHandler(range, RECORD_LIMIT)
  let filtersPayload = {
    $where: {
      ...buildWhereQuery(FILTER_KEYS.EQUAL, 'assignment_id', props.id),
    },
  }

  let payload = { ...paginationPayload, ...filtersPayload }
  const pageNo = getPageNumberWithRange(payload)
  const isAlreadyCached = studentsList.value.hasOwnProperty(pageNo)

  if (isAlreadyCached) {
    currentStudentList.value = studentsList.value[pageNo]
  } else {
    await filterAssignmentStudents(payload).then((response) => {
      const records = response.data.records
      studentCounts.value = response.data.meta.total_records
      currentStudentList.value = records
      studentsList.value = {
        ...studentsList.value,
        [pageNo]: records,
      }
    })
  }

  currentStudentList.value.forEach((assignment) => fullName(assignment.student))

  isLoading.value = false
}
const getPayload = () => {
  let resultList = []
  let studentList = Object.values(studentsList.value).flat()
  studentList.forEach((user) => {
    if (user.obtained_marks || user.obtained_marks === 0)
      resultList.push({
        obtained_marks: user.obtained_marks,
        evaluator_comment: user.evaluator_comment || '',
        id: user.id,
      })
  })
  return resultList
}

const addAssignmentMarks = () => {
  isDataSubmitting.value = true
  const payload = { student_assignment_marks: getPayload() }
  addStudentsAssignmentMarks(payload)
    .then(() => {
      store.commit('toast/NEW', { message: 'Assignment Received Successfully', type: 'success' })
      handleClick('close', 'fetch')
    })
    .finally(() => {
      isDataSubmitting.value = false
    })
}
const filterAssignmentStudents = (payload) =>
  store.dispatch('assignment/filterAssignmentStudents', payload)
const addStudentsAssignmentMarks = (payload) => {
  return store.dispatch('assignment/addStudentsAssignmentMarks', payload)
}
const isDisabled = (status) => {
  return status !== 'submitted'
}
onMounted(() => {
  getStudentList()
})
</script>
